import {
  createFrontendPlugin,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import {
  BCM_PLAN_ANNOTATION,
  BCM_PLAN_UPDATED_ANNOTATION,
} from '@internal/plugin-bcm-common';
import { GraphHealthProvider } from '../datadog/GraphHealth';
import { isProcess } from './processSupport';
import { processEditorRouteRef, processesRouteRef } from './routes';

/**
 * Business continuity in the portal: SAVR's critical business processes as
 * catalog entities (kind BusinessProcess, see the backend's savr-bcm module),
 * with live system health rolled up across the systems and vendors that
 * support them.
 *
 * Deliberately no bespoke diagram renderer. At five or six unordered
 * dependencies a graph says nothing the table does not, and the stock
 * Relations card already renders the topology with health colouring. A
 * purpose-built diagram is worth it once spec.steps gives a real flow to
 * draw - at which point SupportMatrix already accepts step-shaped rows.
 */

/** /processes - the support matrix. Earns its own sidebar entry via the nav. */
export const processesPage = PageBlueprint.make({
  params: {
    path: '/processes',
    routeRef: processesRouteRef,
    icon: <AccountTreeIcon fontSize="inherit" />,
    title: 'Critical processes',
    loader: async () =>
      import('./ProcessesPage').then(m => <m.ProcessesPage />),
  },
});

/**
 * /processes/:name/edit - draft a process's steps against a live preview.
 *
 * Deliberately NOT in the sidebar, and AppNav drops it twice over: it declares
 * no icon, and an item without one is skipped; and a nav destination is
 * resolved by calling the route ref with no parameters, which a ref that takes
 * a `name` cannot answer. Reached from the Edit flow action on the process's
 * own page instead - there is no such thing as editing "a process" in general.
 *
 * It still needs the route ref, which is what makes the path routable and lets
 * the Flow card link to it.
 */
export const processEditorPage = PageBlueprint.make({
  name: 'editor',
  params: {
    path: '/processes/:name/edit',
    routeRef: processEditorRouteRef,
    title: 'Edit flow',
    loader: async () =>
      import('./ProcessEditorPage').then(m => <m.ProcessEditorPage />),
  },
});

/**
 * The flow: the process's steps in order, with live status per step. Renders
 * nothing when a process has no steps authored, so it costs the seven
 * unmapped processes nothing.
 */
const processFlowCard = EntityCardBlueprint.make({
  name: 'process-flow',
  params: {
    filter: entity => isProcess(entity.kind),
    loader: async () =>
      import('./ProcessFlowCard').then(m => (
        <GraphHealthProvider>
          <m.ProcessFlowCard />
        </GraphHealthProvider>
      )),
  },
});

/** Per-dependency status, MTPD and measured outage on the process overview. */
const processSupportCard = EntityCardBlueprint.make({
  name: 'process-support',
  params: {
    filter: entity => isProcess(entity.kind),
    loader: async () =>
      import('./ProcessSupportCard').then(m => (
        // The provider owns the shared 60s health-summary fetch that
        // HealthNameBox and the roll-up both read.
        <GraphHealthProvider>
          <m.ProcessSupportCard />
        </GraphHealthProvider>
      )),
  },
});

/** What the BIA declares: owner, priority, MTPD/RTO, source. */
const processAboutCard = EntityCardBlueprint.make({
  name: 'process-about',
  params: {
    type: 'info',
    filter: entity => isProcess(entity.kind),
    loader: async () =>
      import('./ProcessAboutCard').then(m => <m.ProcessAboutCard />),
  },
});

/**
 * The continuity plan behind an entity - link, last updated, whether the
 * annual review is due - from the savr.com/bcm-plan annotations.
 *
 * Every BusinessProcess, since one with no plan declared is itself the
 * warning; plus any other kind that carries either annotation, since a
 * System or Component may be covered by a plan directly, and a date beside
 * a mis-keyed link is a gap the card has to show. Filtered here as well as
 * in the card so the hundreds of pages outside the BCM set never fetch the
 * chunk.
 */
export const bcmPlanCard = EntityCardBlueprint.make({
  name: 'bcm-plan',
  params: {
    type: 'info',
    filter: entity =>
      isProcess(entity.kind) ||
      Boolean(entity.metadata.annotations?.[BCM_PLAN_ANNOTATION]) ||
      Boolean(entity.metadata.annotations?.[BCM_PLAN_UPDATED_ANNOTATION]),
    loader: async () => import('./BcmPlanCard').then(m => <m.BcmPlanCard />),
  },
});

export const bcmPlugin = createFrontendPlugin({
  pluginId: 'bcm',
  routes: {
    processes: processesRouteRef,
    processEditor: processEditorRouteRef,
  },
  extensions: [
    processesPage,
    processEditorPage,
    processFlowCard,
    processSupportCard,
    processAboutCard,
    bcmPlanCard,
  ],
});
